import { useEffect, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { apiFetch } from '../utils/api';

// Global auth state caches (separate for user vs superadmin contexts)
let globalAuthStateUser: boolean | null = null;
let isAuthInitializedUser = false;
let globalAuthStateSuperadmin: boolean | null = null;
let isAuthInitializedSuperadmin = false;

// Function to reset auth cache (called when user logs out or token becomes invalid)
export function resetAuthCache() {
  globalAuthStateUser = null;
  isAuthInitializedUser = false;
  globalAuthStateSuperadmin = null;
  isAuthInitializedSuperadmin = false;
}

export default function AuthWrapper({ children }) {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const isSuperadminContext = currentPath.startsWith('/superadmin');
  const isPublicRoute = currentPath === '/login' || currentPath === '/superadmin-login';

  const [isLoading, setIsLoading] = useState(
    isSuperadminContext ? !isAuthInitializedSuperadmin : !isAuthInitializedUser
  );
  const [authenticated, setAuthenticated] = useState(
    (isSuperadminContext ? globalAuthStateSuperadmin : globalAuthStateUser) || false
  );

  useEffect(() => {
    // Skip auth check for public routes
    if (isPublicRoute) {
      setIsLoading(false);
      return;
    }

    // Check authentication status
    const checkAuth = async () => {
      const cachedInitialized = isSuperadminContext ? isAuthInitializedSuperadmin : isAuthInitializedUser;
      const cachedState = isSuperadminContext ? globalAuthStateSuperadmin : globalAuthStateUser;

      // If we already have a cached auth state, use it immediately
      if (cachedInitialized && cachedState !== null) {
        setAuthenticated(!!cachedState);
        setIsLoading(false);

        if (!cachedState) {
          navigate({ to: isSuperadminContext ? '/superadmin-login' : '/login' });
        }
        return;
      }

      let isAuth = false;
      try {
        const endpoint = isSuperadminContext ? '/api/auth/superadmin/me' : '/api/auth/me';
        const resp = await apiFetch(endpoint);
        isAuth = resp.ok;
      } catch {
        isAuth = false;
      }

      // Cache the auth state globally
      if (isSuperadminContext) {
        globalAuthStateSuperadmin = isAuth;
        isAuthInitializedSuperadmin = true;
      } else {
        globalAuthStateUser = isAuth;
        isAuthInitializedUser = true;
      }

      setAuthenticated(isAuth);
      setIsLoading(false);

      // Redirect to login if not authenticated
      if (!isAuth) {
        navigate({ to: isSuperadminContext ? '/superadmin-login' : '/login' });
      }
    };

    checkAuth();
  }, [navigate, currentPath, isPublicRoute, isSuperadminContext]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If on a public route, always show the page
  if (isPublicRoute) {
    return children;
  }

  // If authenticated, show the protected content
  if (authenticated) {
    return children;
  }

  // If not authenticated and not on public route, show nothing (redirect will happen)
  return null;
}
